import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Shield, Users, Settings, X, UserPlus, Trash2, Search, Star, Bell, BellRing, Mail, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/custom/ConfirmDialog';
import {
  fetchOperatorsFull,
  fetchAdminGroups,
  createGroupApi,
  updateGroupApi,
  deleteGroupApi,
  fetchGroupMembers,
  addGroupMembers,
  removeGroupMember,
  fetchGroupConfig,
  updateGroupConfig,
  fetchGroupNotificationConfig,
  updateGroupNotificationConfig,
} from '@/hooks/useAdmin';
import { apiFetch } from '@/lib/api-client';
import { GROUP_DEFAULT_OFF_PREFS } from '@uniticket/shared';
import type {
  OperatorAdminItem,
  GroupAdminItem,
  GroupMemberItem,
  CreateGroupRequest,
  UpdateGroupRequest,
  TicketTypePermission,
} from '@uniticket/shared';

const GROUP_DEFAULT_OFF = GROUP_DEFAULT_OFF_PREFS as readonly string[];

const TICKET_TYPE_LABELS: Record<string, string> = {
  OPEN_ORDER: 'Open Order',
  ADDRESS_MANAGEMENT: 'Address Mgmt',
  SUPPORT_REQUEST: 'Support',
  BUILD: 'Build Mgmt',
};

/**
 * Ticket types the config dialog can grant. Single source for the dialog's initial state, its
 * load merge and its radio rows — those three MUST agree: handleSave rebuilds the whole
 * permissions object from local state and PUTs it, so a type missing from this list would have
 * its existing grant silently deleted whenever an unrelated change is saved.
 *
 * Note a grant here is still capped by the operator's ceiling
 * (tt_operators.allowed_ticket_types), so enabling BUILD for a group also requires enabling it
 * for that operator in Operator Settings — otherwise this has no effect.
 */
const CONFIGURABLE_TICKET_TYPES = ['OPEN_ORDER', 'ADDRESS_MANAGEMENT', 'SUPPORT_REQUEST', 'BUILD'] as const;

const PERMISSION_LABELS: Record<string, string> = {
  read: 'R',
  create: 'C',
  'read+create': 'R+C',
};

const PERMISSION_OPTIONS: { value: TicketTypePermission | 'none'; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'read', label: 'Read' },
  { value: 'create', label: 'Create' },
  { value: 'read+create', label: 'Read + Create' },
];

export function GroupManagement() {
  const { t } = useTranslation('admin');
  const [groups, setGroups] = useState<GroupAdminItem[]>([]);
  const [operators, setOperators] = useState<OperatorAdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<GroupAdminItem | null>(null);
  const [membersDialog, setMembersDialog] = useState<GroupAdminItem | null>(null);
  const [configDialog, setConfigDialog] = useState<GroupAdminItem | null>(null);
  const [notifDialog, setNotifDialog] = useState<GroupAdminItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GroupAdminItem | null>(null);
  const [filterOperator, setFilterOperator] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (filterOperator) params.operatorId = filterOperator;
      if (filterActive) params.active = filterActive;
      if (search) params.q = search;

      const [groupsRes, opsRes] = await Promise.all([
        fetchAdminGroups(params),
        operators.length > 0 ? Promise.resolve({ data: operators }) : fetchOperatorsFull(),
      ]);
      setGroups(groupsRes.data);
      if (operators.length === 0) setOperators(opsRes.data);
    } catch (err: any) {
      toast.error(err.message || t('groups.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, filterOperator, filterActive, search, operators]);

  useEffect(() => { loadData(); }, [loadData]);

  function handleAdd() {
    setEditing(null);
    setDialogOpen(true);
  }

  function handleEdit(group: GroupAdminItem) {
    setEditing(group);
    setDialogOpen(true);
  }

  async function handleSave(data: CreateGroupRequest | UpdateGroupRequest) {
    try {
      if (editing) {
        await updateGroupApi(editing.id, data as UpdateGroupRequest);
        toast.success(t('groups.updated'));
      } else {
        await createGroupApi(data as CreateGroupRequest);
        toast.success(t('groups.created'));
      }
      setDialogOpen(false);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('groups.saveError'));
    }
  }

  async function handleSetDefault(group: GroupAdminItem) {
    try {
      await updateGroupApi(group.id, { isDefault: true });
      toast.success(t('groups.defaultSet'));
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('groups.defaultError'));
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    try {
      await deleteGroupApi(deleteTarget.id);
      toast.success(t('groups.deleted'));
      setDeleteTarget(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('groups.deleteError'));
      setDeleteTarget(null);
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">{t('groups.title')}</h2>
            <p className="text-sm text-slate-500">{t('groups.count', { count: groups.length })}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="bg-brand-gradient text-white hover:opacity-90">
          <Plus className="w-4 h-4 mr-2" />
          {t('groups.add')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search groups..."
            className="pl-9"
          />
        </div>
        <select
          value={filterOperator}
          onChange={(e) => setFilterOperator(e.target.value)}
          className="h-10 px-3 border border-slate-200 rounded-lg text-sm text-slate-600 bg-white"
        >
          <option value="">All Operators</option>
          {operators.map((op) => (
            <option key={op.id} value={op.id}>{op.name}</option>
          ))}
        </select>
        <select
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value)}
          className="h-10 px-3 border border-slate-200 rounded-lg text-sm text-slate-600 bg-white"
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('groups.cols.name')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('groups.cols.operator')}</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('groups.cols.members')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('groups.cols.permissions')}</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('groups.cols.default')}</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('groups.cols.status')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('groups.cols.created')}</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('groups.cols.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {groups.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                    {t('groups.empty')}
                  </td>
                </tr>
              ) : (
                groups.map((group) => (
                  <tr key={group.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <span className="font-medium text-slate-700">{group.name}</span>
                        {group.description && (
                          <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[200px]">{group.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{group.operatorCode}</span>
                      <span className="ml-1.5">{group.operatorName}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">{group.memberCount}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {Object.entries(group.ticketTypePermissions).map(([type, perm]) => (
                          <span
                            key={type}
                            className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-brand-rose/10 text-brand-rose"
                          >
                            {TICKET_TYPE_LABELS[type] || type}: {PERMISSION_LABELS[perm] || perm}
                          </span>
                        ))}
                        {Object.keys(group.ticketTypePermissions).length === 0 && (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {group.isDefault ? (
                        <span className="inline-flex items-center text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Default</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {group.isActive ? (
                        <span className="inline-flex items-center text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Active</span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{new Date(group.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!group.isDefault && (
                          <button
                            onClick={() => handleSetDefault(group)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
                            title={t('groups.setDefault')}
                          >
                            <Star className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(group)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-brand-rose hover:bg-brand-rose/5 transition-colors"
                          title={t('groups.editTitle')}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setMembersDialog(group)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-brand-purple hover:bg-brand-purple/5 transition-colors"
                          title={t('groups.membersTitle')}
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setConfigDialog(group)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-brand-orange hover:bg-brand-orange/5 transition-colors"
                          title={t('groups.configTitle')}
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setNotifDialog(group)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                          title={t('groups.notificationDefaults')}
                        >
                          <Bell className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(group)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title={t('groups.deleteTitle')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialogs */}
      {dialogOpen && (
        <GroupDialog
          editing={editing}
          operators={operators}
          onSave={handleSave}
          onClose={() => setDialogOpen(false)}
        />
      )}
      {membersDialog && (
        <MembersDialog
          group={membersDialog}
          onClose={() => { setMembersDialog(null); loadData(); }}
        />
      )}
      {configDialog && (
        <ConfigDialog
          group={configDialog}
          operators={operators}
          onClose={() => { setConfigDialog(null); loadData(); }}
        />
      )}
      {notifDialog && (
        <NotificationDefaultsDialog
          group={notifDialog}
          onClose={() => setNotifDialog(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title={t('groups.deleteTitle')}
          message={t('groups.deleteConfirm', { name: deleteTarget.name })}
          details={deleteTarget.memberCount > 0
            ? [`This group has ${deleteTarget.memberCount} member(s) who will be removed from it.`]
            : undefined
          }
          confirmLabel={t('groups.delete')}
          cancelLabel={t('groups.cancel')}
          variant="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// ── Group Dialog ────────────────────────────────────────────

interface GroupDialogProps {
  editing: GroupAdminItem | null;
  operators: OperatorAdminItem[];
  onSave: (data: CreateGroupRequest | UpdateGroupRequest) => Promise<void>;
  onClose: () => void;
}

function GroupDialog({ editing, operators, onSave, onClose }: GroupDialogProps) {
  const { t } = useTranslation('admin');
  const [name, setName] = useState(editing?.name || '');
  const [operatorId, setOperatorId] = useState(editing?.operatorId || (operators[0]?.id ?? ''));
  const [description, setDescription] = useState(editing?.description || '');
  const [isDefault, setIsDefault] = useState(editing?.isDefault ?? false);
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [sharedMailboxEmail, setSharedMailboxEmail] = useState(editing?.sharedMailboxEmail || '');
  const [routeToSharedMailbox, setRouteToSharedMailbox] = useState(editing?.routeToSharedMailbox ?? false);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name) {
      toast.error('Group name is required');
      return;
    }
    if (routeToSharedMailbox && !sharedMailboxEmail.trim()) {
      toast.error(t('groups.sharedMailboxRequired'));
      return;
    }
    const sharedMailbox = sharedMailboxEmail.trim() || null;
    setSaving(true);
    try {
      if (editing) {
        await onSave({ name, description: description || undefined, isDefault, isActive, sharedMailboxEmail: sharedMailbox, routeToSharedMailbox } satisfies UpdateGroupRequest);
      } else {
        await onSave({ name, operatorId, description: description || undefined, isDefault, sharedMailboxEmail: sharedMailbox, routeToSharedMailbox } satisfies CreateGroupRequest);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="gradient-band px-6 py-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">
            {editing ? t('groups.editTitle') : t('groups.createTitle')}
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[calc(100vh-8rem)] overflow-y-auto">
          <div className="space-y-1.5">
            <Label>{t('groups.fields.name')}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('groups.fields.operator')}</Label>
            {editing ? (
              <Input value={`${editing.operatorCode} — ${editing.operatorName}`} disabled className="bg-slate-50" />
            ) : (
              <select
                value={operatorId}
                onChange={(e) => setOperatorId(e.target.value)}
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white"
                required
              >
                {operators.map((op) => (
                  <option key={op.id} value={op.id}>{op.code} — {op.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('groups.fields.description')} <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('groups.fields.sharedMailbox')} <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input
              type="email"
              value={sharedMailboxEmail}
              onChange={(e) => setSharedMailboxEmail(e.target.value)}
              placeholder={t('groups.fields.sharedMailboxPlaceholder')}
              maxLength={255}
            />
            <p className="text-xs text-slate-400">{t('groups.fields.sharedMailboxDesc')}</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setRouteToSharedMailbox(!routeToSharedMailbox)}
              className={`relative w-11 h-6 rounded-full transition-colors ${routeToSharedMailbox ? 'bg-brand-rose' : 'bg-slate-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${routeToSharedMailbox ? 'translate-x-5' : ''}`} />
            </button>
            <Label className="cursor-pointer" onClick={() => setRouteToSharedMailbox(!routeToSharedMailbox)}>
              {t('groups.fields.routeToSharedMailbox')}
            </Label>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsDefault(!isDefault)}
              className={`relative w-11 h-6 rounded-full transition-colors ${isDefault ? 'bg-blue-500' : 'bg-slate-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isDefault ? 'translate-x-5' : ''}`} />
            </button>
            <Label className="cursor-pointer" onClick={() => setIsDefault(!isDefault)}>
              {t('groups.fields.isDefault')}
            </Label>
          </div>

          {editing && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsActive(!isActive)}
                className={`relative w-11 h-6 rounded-full transition-colors ${isActive ? 'bg-green-500' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-5' : ''}`} />
              </button>
              <Label className="cursor-pointer" onClick={() => setIsActive(!isActive)}>
                {isActive ? 'Active' : 'Inactive'}
              </Label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>{t('groups.cancel')}</Button>
            <Button type="submit" disabled={saving} className="bg-brand-gradient text-white hover:opacity-90">
              {saving ? t('groups.saving') : editing ? t('groups.update') : t('groups.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Members Dialog ──────────────────────────────────────────

interface MembersDialogProps {
  group: GroupAdminItem;
  onClose: () => void;
}

function MembersDialog({ group, onClose }: MembersDialogProps) {
  const { t } = useTranslation('admin');
  const [members, setMembers] = useState<GroupMemberItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<{ id: string; displayName: string; email: string }[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [memberSearch, setMemberSearch] = useState('');

  const filteredAvailableUsers = useMemo(() => {
    const q = memberSearch.toLowerCase().trim();
    if (!q) return availableUsers;
    return availableUsers.filter((u) => u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [availableUsers, memberSearch]);

  const loadMembers = useCallback(async () => {
    try {
      const res = await fetchGroupMembers(group.id);
      setMembers(res.data);
    } catch (err: any) {
      toast.error(err.message || t('groups.loadError'));
    } finally {
      setLoading(false);
    }
  }, [group.id, t]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  async function handleShowAdd() {
    setAdding(true);
    try {
      // Fetch users for same operator
      const res = await apiFetch<{ data: { id: string; displayName: string; email: string; role: string }[] }>('/users/all');
      // Filter to same operator — but /users/all returns all active users, so we filter client-side
      // Actually we need users from this operator. Let's fetch admin users with operator filter.
      const adminRes = await apiFetch<{ data: { id: string; email: string; displayName: string; operatorId: string }[] }>(
        `/admin/users?operatorId=${group.operatorId}&limit=200`,
      );
      const memberIds = new Set(members.map((m) => m.userId));
      const available = adminRes.data.filter((u: any) => !memberIds.has(u.id));
      setAvailableUsers(available);
    } catch {
      toast.error('Failed to load users');
    }
  }

  async function handleAddMember() {
    if (!selectedUserId) return;
    try {
      await addGroupMembers(group.id, [selectedUserId]);
      toast.success(t('groups.memberAdded'));
      setAdding(false);
      setSelectedUserId('');
      loadMembers();
    } catch (err: any) {
      toast.error(err.message || t('groups.saveError'));
    }
  }

  async function handleRemoveMember(userId: string) {
    try {
      await removeGroupMember(group.id, userId);
      toast.success(t('groups.memberRemoved'));
      loadMembers();
    } catch (err: any) {
      toast.error(err.message || t('groups.saveError'));
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden">
        <div className="gradient-band px-6 py-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">
            {t('groups.membersTitle')} — {group.name}
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
          {/* Add member */}
          {!adding ? (
            <Button onClick={handleShowAdd} variant="outline" size="sm">
              <UserPlus className="w-4 h-4 mr-2" />
              {t('groups.addMember')}
            </Button>
          ) : (
            <div className="p-3 bg-slate-50 rounded-lg space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder={t('groups.searchMembers')}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 transition-all placeholder:text-slate-400"
                />
              </div>
              <div className="max-h-40 overflow-y-auto space-y-0.5">
                {filteredAvailableUsers.length === 0 ? (
                  <p className="text-xs text-slate-400 px-2 py-2">{t('groups.noUsersFound')}</p>
                ) : (
                  filteredAvailableUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => setSelectedUserId(u.id === selectedUserId ? '' : u.id)}
                      className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${u.id === selectedUserId ? 'bg-brand-rose/10 text-brand-rose font-medium' : 'text-slate-700 hover:bg-white'}`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold ${u.id === selectedUserId ? 'bg-brand-rose/20 text-brand-rose' : 'bg-slate-200 text-slate-600'}`}>
                        {u.displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="block truncate">{u.displayName}</span>
                        <span className="block text-xs text-slate-400 truncate">{u.email}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
              <div className="flex items-center gap-2 pt-1 border-t border-slate-200">
                <Button onClick={handleAddMember} disabled={!selectedUserId} size="sm" className="bg-brand-gradient text-white hover:opacity-90">
                  {t('groups.addMember')}
                </Button>
                <Button onClick={() => { setAdding(false); setMemberSearch(''); setSelectedUserId(''); }} variant="outline" size="sm">
                  {t('groups.cancel')}
                </Button>
              </div>
            </div>
          )}

          {/* Members table */}
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : members.length === 0 ? (
            <p className="text-center text-slate-400 py-8">{t('groups.noMembers')}</p>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-400">Name</th>
                    <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-400">Email</th>
                    <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-400">Role</th>
                    <th className="w-16 text-right px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {members.map((m) => (
                    <tr key={m.userId} className="hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-slate-700">{m.displayName}</td>
                      <td className="px-4 py-2 text-slate-500">{m.email}</td>
                      <td className="px-4 py-2">
                        <span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">{m.role}</span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => handleRemoveMember(m.userId)}
                          className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Remove member"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={onClose}>{t('groups.cancel')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Config Dialog ───────────────────────────────────────────

interface ConfigDialogProps {
  group: GroupAdminItem;
  operators: OperatorAdminItem[];
  onClose: () => void;
}

function ConfigDialog({ group, operators, onClose }: ConfigDialogProps) {
  const { t } = useTranslation('admin');
  const [permissions, setPermissions] = useState<Record<string, TicketTypePermission | 'none'>>(
    Object.fromEntries(CONFIGURABLE_TICKET_TYPES.map((tt) => [tt, 'none'])),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmCeiling, setConfirmCeiling] = useState(false);

  // A group grant is intersected with the operator's allowed_ticket_types, so a permission for a
  // type the operator is not entitled to saves fine and then does nothing. Surface that instead of
  // letting it fail silently. `undefined` (operator not in the loaded list) means "don't guess".
  const operatorAllowed = operators.find((o) => o.id === group.operatorId)?.allowedTicketTypes;
  const isEntitled = (type: string) => !operatorAllowed || operatorAllowed.includes(type);
  const ineffective = CONFIGURABLE_TICKET_TYPES.filter(
    (type) => permissions[type] !== 'none' && !isEntitled(type),
  );

  useEffect(() => {
    async function load() {
      try {
        const res = await fetchGroupConfig(group.id);
        const existing = (res.data.ticketTypePermissions || {}) as Record<string, TicketTypePermission>;
        setPermissions(
          Object.fromEntries(CONFIGURABLE_TICKET_TYPES.map((tt) => [tt, existing[tt] || 'none'])),
        );
      } catch (err: any) {
        toast.error(err.message || t('groups.loadError'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [group.id, t]);

  function handleSaveClick() {
    if (ineffective.length > 0 && !confirmCeiling) {
      setConfirmCeiling(true);
      return;
    }
    void handleSave();
  }

  async function handleSave() {
    setConfirmCeiling(false);
    setSaving(true);
    try {
      // Build permissions object (exclude 'none' entries)
      const ticketTypePermissions: Record<string, TicketTypePermission> = {};
      const visibleTicketTypes: string[] = [];
      for (const [type, perm] of Object.entries(permissions)) {
        if (perm !== 'none') {
          ticketTypePermissions[type] = perm as TicketTypePermission;
          visibleTicketTypes.push(type);
        }
      }
      await updateGroupConfig(group.id, { ticketTypePermissions, visibleTicketTypes });
      toast.success(t('groups.configUpdated'));
      onClose();
    } catch (err: any) {
      toast.error(err.message || t('groups.saveError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="gradient-band px-6 py-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">
            {t('groups.configTitle')} — {group.name}
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[calc(100vh-10rem)] overflow-y-auto">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500">
                {t('groups.configDescription')}
              </p>

              <div className="space-y-3">
                {CONFIGURABLE_TICKET_TYPES.map((type) => (
                  <div key={type} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium text-slate-700">
                      {TICKET_TYPE_LABELS[type]}
                      {/* Shown before the mistake is made, not only after. */}
                      {!isEntitled(type) && (
                        <span className="block text-[11px] font-normal text-amber-600 mt-0.5">
                          {t('groups.typeNotEnabledForOperator', { operator: group.operatorName })}
                        </span>
                      )}
                    </span>
                    <select
                      value={permissions[type]}
                      onChange={(e) => setPermissions({ ...permissions, [type]: e.target.value as TicketTypePermission | 'none' })}
                      className="h-9 px-3 border border-slate-200 rounded-lg text-sm bg-white min-w-[140px]"
                    >
                      {PERMISSION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {ineffective.length > 0 && (
                <div className="flex gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    {t('groups.ceilingWarning', {
                      types: ineffective.map((tt) => TICKET_TYPE_LABELS[tt]).join(', '),
                      operator: group.operatorName,
                    })}
                  </p>
                </div>
              )}
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose}>{t('groups.cancel')}</Button>
            <Button
              onClick={handleSaveClick}
              disabled={saving || loading}
              className="bg-brand-gradient text-white hover:opacity-90"
            >
              {saving ? t('groups.saving') : t('groups.saveConfig')}
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation shown when a granted type is outside the operator's entitlement. The grant is
          still saved — it simply has no effect until the type is enabled in Operator Settings. */}
      {confirmCeiling && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 flex items-start gap-3 border-b border-slate-100">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <h3 className="text-sm font-bold text-slate-900">{t('groups.ceilingConfirmTitle')}</h3>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-slate-600 leading-relaxed">
                {t('groups.ceilingWarning', {
                  types: ineffective.map((tt) => TICKET_TYPE_LABELS[tt]).join(', '),
                  operator: group.operatorName,
                })}
              </p>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t('groups.ceilingConfirmHint')}
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 bg-slate-50">
              <Button variant="outline" onClick={() => setConfirmCeiling(false)}>
                {t('groups.ceilingConfirmBack')}
              </Button>
              <Button onClick={handleSave} className="bg-brand-gradient text-white hover:opacity-90">
                {t('groups.ceilingConfirmSave')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Notification Defaults Dialog ────────────────────────────

const NOTIF_PREF_KEYS: { emailKey: string; inappKey: string; label: string; desc: string }[] = [
  { emailKey: 'ticket_created', inappKey: 'inapp_ticket_created', label: 'Ticket Created', desc: 'Confirmation when a ticket is created' },
  { emailKey: 'ticket_assigned', inappKey: 'inapp_ticket_assigned', label: 'Ticket Assigned', desc: 'When a ticket is assigned' },
  { emailKey: 'comment_on_tickets', inappKey: 'inapp_comment_on_tickets', label: 'New Comments', desc: 'When someone comments on tickets' },
  { emailKey: 'status_change', inappKey: 'inapp_status_change', label: 'Status Changes', desc: 'When a ticket status is updated' },
  { emailKey: 'sla_warning', inappKey: 'inapp_sla_warning', label: 'SLA Warnings', desc: 'When a ticket approaches its SLA deadline' },
  { emailKey: 'sla_breach', inappKey: 'inapp_sla_breach', label: 'SLA Breaches', desc: 'When a ticket SLA deadline is breached' },
  { emailKey: 'escalation', inappKey: 'inapp_escalation', label: 'Escalations', desc: 'When a ticket is escalated' },
  { emailKey: 'ticket_reopened', inappKey: 'inapp_ticket_reopened', label: 'Ticket Reopened', desc: 'When a closed ticket is reopened' },
  { emailKey: 'mentioned', inappKey: 'inapp_mentioned', label: 'Mentions', desc: 'When a user is @mentioned in a comment' },
];

interface NotificationDefaultsDialogProps {
  group: GroupAdminItem;
  onClose: () => void;
}

function NotifToggle({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative w-10 h-5 rounded-full transition-colors ${enabled ? 'bg-brand-rose' : 'bg-slate-300'}`}
      role="switch"
      aria-checked={enabled}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${
          enabled ? 'translate-x-5' : ''
        }`}
      />
    </button>
  );
}

function NotificationDefaultsDialog({ group, onClose }: NotificationDefaultsDialogProps) {
  const { t } = useTranslation('admin');
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetchGroupNotificationConfig(group.id);
        setPrefs(res.data ?? {});
      } catch (err: any) {
        toast.error(err.message || t('groups.loadError'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [group.id, t]);

  // Group-level default for a pref key (high-frequency email events default OFF; everything else ON)
  function defaultFor(key: string) {
    return !GROUP_DEFAULT_OFF.includes(key);
  }

  function handleToggle(key: string) {
    setPrefs((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? defaultFor(key)),
    }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateGroupNotificationConfig(group.id, prefs);
      toast.success(t('groups.notificationDefaultsSaved'));
      onClose();
    } catch (err: any) {
      toast.error(err.message || t('groups.saveError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        <div className="gradient-band px-6 py-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">
            {t('groups.notificationDefaultsTitle')} — {group.name}
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
          <p className="text-sm text-slate-500">
            {t('groups.notificationDefaultsDesc')}
          </p>
          <p className="text-xs text-slate-400">
            {t('groups.notificationDefaultsHint', 'Toggles default to on. User preferences override these defaults.')}
          </p>
          {group.routeToSharedMailbox && group.sharedMailboxEmail && (
            <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">
              {t('groups.notificationDefaultsSharedMailboxBanner', { email: group.sharedMailboxEmail })}
            </div>
          )}

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (
            <>
              {/* Column headers */}
              <div className="flex items-center px-2">
                <div className="flex-1" />
                <div className="flex items-center gap-6">
                  <div className="w-16 flex flex-col items-center gap-1">
                    <BellRing className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">In-App</span>
                  </div>
                  <div className="w-16 flex flex-col items-center gap-1">
                    <Mail className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Email</span>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                {NOTIF_PREF_KEYS.map((item) => (
                  <div key={item.emailKey} className="px-4 py-3 flex items-center bg-white hover:bg-slate-50 transition-colors">
                    <div className="flex-1 space-y-0.5 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{item.label}</p>
                      <p className="text-xs text-slate-400">{item.desc}</p>
                    </div>
                    <div className="flex items-center gap-6 shrink-0">
                      <div className="w-16 flex justify-center">
                        <NotifToggle
                          enabled={prefs[item.inappKey] ?? defaultFor(item.inappKey)}
                          onToggle={() => handleToggle(item.inappKey)}
                        />
                      </div>
                      <div className="w-16 flex justify-center">
                        <NotifToggle
                          enabled={prefs[item.emailKey] ?? defaultFor(item.emailKey)}
                          onToggle={() => handleToggle(item.emailKey)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose}>{t('groups.cancel')}</Button>
            <Button
              onClick={handleSave}
              disabled={saving || loading}
              className="bg-brand-gradient text-white hover:opacity-90"
            >
              {saving ? t('groups.saving') : t('groups.notificationDefaults')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
